import { useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from 'core/api/queryKeys';
import { setExpectedRevision } from 'core/api/revisionStore';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError, mapFieldErrors } from 'core/errors/mapFieldErrors';
import { Button } from 'shared/components/Button';
import { ConflictAlert, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { ValidationSummary, focusFirstInvalid } from 'shared/components/ValidationSummary';
import { useToast } from 'shared/components/Toast';
import { BilingualField, SelectField, SwitchField, WeightInput } from 'shared/forms/Fields';
import { isValidWeight, localizedName, parseWeight } from 'shared/utilities/format';
import { useUnsavedChanges } from 'shared/utilities/useUnsavedChanges';

let answerKey = 0;

const emptyAnswer = (patterns) => ({
  localKey: `new-${answerKey += 1}`,
  id: null,
  text_ar: '',
  text_en: '',
  is_active: true,
  weights: patterns.map((pattern) => ({ professional_pattern_id: pattern.id, weight: '0.00' })),
});

function toPayload(form) {
  return {
    assessment_section_id: Number(form.assessment_section_id),
    text_ar: form.text_ar,
    text_en: form.text_en,
    explanation_ar: form.explanation_ar || null,
    explanation_en: form.explanation_en || null,
    is_active: form.is_active,
    answers: form.answers.map((answer) => ({
      id: answer.id || null,
      text_ar: answer.text_ar,
      text_en: answer.text_en,
      is_active: answer.is_active,
      weights: answer.weights.map((weight) => ({
        professional_pattern_id: weight.professional_pattern_id,
        weight: parseWeight(weight.weight),
      })),
    })),
  };
}

export function QuestionEditorPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [params] = useSearchParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    assessment_section_id: params.get('section_id') || '',
    text_ar: '',
    text_en: '',
    explanation_ar: '',
    explanation_en: '',
    is_active: true,
    answers: [],
  });
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  const [conflict, setConflict] = useState(false);
  useUnsavedChanges(dirty);

  const sectionsQuery = useQuery({ queryKey: queryKeys.sections, queryFn: ({ signal }) => assessmentApi.listSections({ signal }) });
  const patternsQuery = useQuery({ queryKey: queryKeys.patterns, queryFn: ({ signal }) => assessmentApi.listPatterns({ signal }) });
  const questionQuery = useQuery({
    queryKey: queryKeys.question(id),
    enabled: isEdit,
    queryFn: ({ signal }) => assessmentApi.showQuestion(id, { signal }),
  });

  const patterns = useMemo(() => patternsQuery.data?.items || [], [patternsQuery.data]);

  if (!isEdit && patterns.length > 0 && form.answers.length === 0) {
    setForm((current) => (
      current.answers.length === 0
        ? { ...current, answers: [emptyAnswer(patterns), emptyAnswer(patterns)] }
        : current
    ));
  }

  useEffect(() => {
    const question = questionQuery.data?.question || questionQuery.data;
    if (!question || !patterns.length || dirty) {
      return;
    }
    setForm({
      assessment_section_id: String(question.assessment_section_id),
      text_ar: question.text_ar || '',
      text_en: question.text_en || '',
      explanation_ar: question.explanation_ar || '',
      explanation_en: question.explanation_en || '',
      is_active: Boolean(question.is_active),
      answers: (question.answers || []).map((answer) => ({
        localKey: `answer-${answer.id}`,
        id: answer.id,
        text_ar: answer.text_ar || '',
        text_en: answer.text_en || '',
        is_active: Boolean(answer.is_active),
        weights: patterns.map((pattern) => ({
          professional_pattern_id: pattern.id,
          weight: String(answer.weights?.find((weight) => weight.professional_pattern_id === pattern.id)?.weight ?? ''),
        })),
      })),
    });
  }, [dirty, patterns, questionQuery.data]);

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.questions() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.question(id) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.overview }),
      queryClient.invalidateQueries({ queryKey: ['assessment', 'weight-matrix'] }),
    ]);
  };

  const create = useMutation({
    mutationFn: (body) => assessmentApi.createQuestion(body),
    onSuccess: async () => {
      flushSync(() => setDirty(false));
      await invalidate();
      toast.success(t('status.created'));
      navigate(paths.builder);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isStaleRevision()) {
        setConflict(true);
        if (error.errors?.current_revision) {
          setExpectedRevision(error.errors.current_revision);
        }
        toast.error(t('errors.staleRevision'));
        return;
      }
      if (error instanceof ApiError && error.isValidation()) {
        setErrors(mapFieldErrors(error.errors));
      }
      toast.error(humanizeApiError(error, t));
    },
  });

  const update = useMutation({
    mutationFn: (body) => assessmentApi.replaceQuestionConfiguration(id, body),
    onSuccess: async () => {
      flushSync(() => {
        setDirty(false);
        setConflict(false);
      });
      await invalidate();
      toast.success(t('status.updated'));
      navigate(paths.builder);
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.isStaleRevision()) {
        setConflict(true);
        if (error.errors?.current_revision) {
          setExpectedRevision(error.errors.current_revision);
        }
        await questionQuery.refetch();
        toast.error(t('errors.staleRevision'));
        return;
      }
      if (error instanceof ApiError && error.isValidation()) {
        setErrors(mapFieldErrors(error.errors));
      }
      toast.error(humanizeApiError(error, t));
    },
  });

  const change = (patch) => {
    setDirty(true);
    setForm((current) => ({ ...current, ...patch }));
  };

  const updateAnswer = (answerIndex, patch) => {
    change({
      answers: form.answers.map((item, index) => (index === answerIndex ? { ...item, ...patch } : item)),
    });
  };

  const moveAnswer = (answerIndex, direction) => {
    const target = answerIndex + direction;
    if (target < 0 || target >= form.answers.length) {
      return;
    }
    const next = [...form.answers];
    const [item] = next.splice(answerIndex, 1);
    next.splice(target, 0, item);
    change({ answers: next });
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setErrors({});
    if (form.answers.length < 2) {
      setErrors({ answers: [t('questions.minAnswers')] });
      focusFirstInvalid({ answers: [t('questions.minAnswers')] }, { answers: 'answers-editor' });
      return;
    }
    const activeAnswers = form.answers.filter((answer) => answer.is_active);
    if (form.is_active && activeAnswers.length < 2) {
      setErrors({ answers: [t('questions.minActiveAnswers')] });
      focusFirstInvalid({ answers: [t('questions.minActiveAnswers')] }, { answers: 'answers-editor' });
      return;
    }
    const invalidWeight = form.answers.some((answer) => answer.weights.some((weight) => !isValidWeight(weight.weight)));
    if (invalidWeight) {
      toast.error(t('errors.validation'));
      return;
    }
    const schema = z.object({
      assessment_section_id: z.coerce.number().int().positive(),
      text_ar: z.string().min(1),
      text_en: z.string().min(1),
      explanation_ar: z.string().optional(),
      explanation_en: z.string().optional(),
      is_active: z.boolean(),
    });
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const next = {};
      parsed.error.issues.forEach((issue) => {
        next[issue.path.join('.')] = [issue.message];
      });
      setErrors(next);
      focusFirstInvalid(next, { assessment_section_id: 'section', text_ar: 'question-text-ar', text_en: 'question-text-en' });
      return;
    }
    const body = toPayload(form);
    if (isEdit) {
      update.mutate(body);
      return;
    }
    create.mutate(body);
  };

  if (sectionsQuery.isLoading || patternsQuery.isLoading || (isEdit && questionQuery.isLoading)) {
    return <Skeleton rows={8} />;
  }
  if (sectionsQuery.isError) {
    return <ErrorState title={humanizeApiError(sectionsQuery.error, t)} onRetry={sectionsQuery.refetch} retryLabel={t('actions.retry')} />;
  }

  const pending = create.isPending || update.isPending;

  return (
    <form className="stack-form" onSubmit={onSubmit} noValidate>
      <PageHeader title={isEdit ? t('actions.edit') : t('builder.newQuestion')} backTo={paths.builder} />
      <ConflictAlert message={conflict ? t('errors.staleRevision') : null} />
      <ValidationSummary errors={errors} title={t('forms.validationSummary')} fieldIds={{ assessment_section_id: 'section', text_ar: 'question-text-ar', text_en: 'question-text-en', answers: 'answers-editor' }} />
      <SelectField
        id="section"
        label={t('questions.section')}
        value={form.assessment_section_id}
        onChange={(value) => change({ assessment_section_id: value })}
        options={(sectionsQuery.data?.items || []).map((section) => ({ value: String(section.id), label: localizedName(section, locale) }))}
        error={errors.assessment_section_id?.[0]}
        required
      />
      <BilingualField
        id="question-text"
        labelAr={t('questions.textAr')}
        labelEn={t('questions.textEn')}
        valueAr={form.text_ar}
        valueEn={form.text_en}
        onChangeAr={(value) => change({ text_ar: value })}
        onChangeEn={(value) => change({ text_en: value })}
        errorAr={errors.text_ar?.[0]}
        errorEn={errors.text_en?.[0]}
        multiline
        required
      />
      <BilingualField
        id="question-explanation"
        labelAr={t('questions.explanationAr')}
        labelEn={t('questions.explanationEn')}
        valueAr={form.explanation_ar}
        valueEn={form.explanation_en}
        onChangeAr={(value) => change({ explanation_ar: value })}
        onChangeEn={(value) => change({ explanation_en: value })}
        errorAr={errors.explanation_ar?.[0]}
        errorEn={errors.explanation_en?.[0]}
        multiline
        required={false}
      />
      <SwitchField id="question-active" label={t('patterns.active')} checked={form.is_active} onChange={(value) => change({ is_active: value })} />
      <div className="answers-editor" id="answers-editor">
        <div className="toolbar">
          <h2>{t('questions.answers')}</h2>
          <Button onClick={() => change({ answers: [...form.answers, emptyAnswer(patterns)] })}>{t('actions.add')}</Button>
        </div>
        {errors.answers?.[0] ? <p className="field-error">{errors.answers[0]}</p> : null}
        <div className="weight-grid-wrap">
          <table className="weight-grid">
            <thead>
              <tr>
                <th className="sticky-col">{t('questions.answers')}</th>
                {patterns.map((pattern) => (
                  <th key={pattern.id}>
                    <span className="color-dot" style={{ background: pattern.color }} />
                    <span>{pattern.name_ar}</span>
                    <span className="muted">{pattern.name_en}</span>
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {form.answers.map((answer, answerIndex) => (
                <tr key={answer.localKey || answer.id || answerIndex}>
                  <td className="sticky-col">
                    <BilingualField
                      id={`answer-${answerIndex}`}
                      labelAr={t('patterns.nameAr')}
                      labelEn={t('patterns.nameEn')}
                      valueAr={answer.text_ar}
                      valueEn={answer.text_en}
                      onChangeAr={(value) => updateAnswer(answerIndex, { text_ar: value })}
                      onChangeEn={(value) => updateAnswer(answerIndex, { text_en: value })}
                      errorAr={errors[`answers.${answerIndex}.text_ar`]?.[0]}
                      errorEn={errors[`answers.${answerIndex}.text_en`]?.[0]}
                      required
                    />
                    <SwitchField
                      id={`answer-active-${answerIndex}`}
                      label={t('questions.answerActive')}
                      checked={answer.is_active}
                      onChange={(value) => updateAnswer(answerIndex, { is_active: value })}
                    />
                  </td>
                  {answer.weights.map((weight, weightIndex) => (
                    <td key={weight.professional_pattern_id}>
                      <WeightInput
                        id={`w-${answerIndex}-${weight.professional_pattern_id}`}
                        value={weight.weight}
                        onChange={(value) => {
                          updateAnswer(answerIndex, {
                            weights: answer.weights.map((row, rowIndex) => (rowIndex === weightIndex ? { ...row, weight: value } : row)),
                          });
                        }}
                        error={errors[`answers.${answerIndex}.weights.${weightIndex}.weight`]?.[0]}
                      />
                    </td>
                  ))}
                  <td>
                    <Button tone="ghost" onClick={() => moveAnswer(answerIndex, -1)} disabled={answerIndex === 0}>
                      {t('actions.moveUp')}
                    </Button>
                    <Button tone="ghost" onClick={() => moveAnswer(answerIndex, 1)} disabled={answerIndex === form.answers.length - 1}>
                      {t('actions.moveDown')}
                    </Button>
                    <Button
                      tone="ghost"
                      onClick={() => change({ answers: form.answers.filter((_, index) => index !== answerIndex) })}
                    >
                      {t('actions.remove')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="editor-actions">
        <Button type="submit" loading={pending} disabled={pending}>{t('actions.save')}</Button>
        <Button tone="ghost" onClick={() => navigate(paths.builder)}>{t('actions.cancel')}</Button>
      </div>
    </form>
  );
}
